import React from 'react';
import { DqSocketContextData, DqSocketRequestOptions } from './types';

const nullFn = () => {};

export function useSocket(
  Context: React.Context<DqSocketContextData>,
  tag: string | undefined,
  created: (...args: any[]) => void = nullFn,
  updated: (...args: any[]) => void = nullFn,
  deleted: (...args: any[]) => void = nullFn,
) {
  const { socket, request, connected } = React.useContext(Context);

  const create = React.useCallback(
    (data: any, options?: DqSocketRequestOptions) => {
      return request(`${tag} create`, data, options);
    },
    [request, tag],
  );

  const update = React.useCallback(
    (data: any, options?: DqSocketRequestOptions) => {
      return request(`${tag} update`, data, options);
    },
    [request, tag],
  );

  const remove = React.useCallback(
    (data: any, options?: DqSocketRequestOptions) => {
      return request(`${tag} delete`, data, options);
    },
    [request, tag],
  );

  const get = React.useCallback(
    (id: string, options?: DqSocketRequestOptions) => {
      return request(`${tag} get`, { _id: id }, options);
    },
    [request, tag],
  );

  const list = React.useCallback(
    (data: any, options?: DqSocketRequestOptions) => {
      return request(`${tag} list`, data, options);
    },
    [request, tag],
  );

  const count = React.useCallback(
    (data: any, options?: DqSocketRequestOptions) => {
      return request(`${tag} count`, data, options);
    },
    [request, tag],
  );

  React.useEffect(() => {
    if (!connected || !socket) return;

    socket.on(`${tag} CREATE`, created);
    socket.on(`${tag} UPDATE`, updated);
    socket.on(`${tag} DELETE`, deleted);

    return () => {
      socket.off(`${tag} CREATE`, created);
      socket.off(`${tag} UPDATE`, updated);
      socket.off(`${tag} DELETE`, deleted);
    };
  }, [created, deleted, socket, tag, updated, connected]);

  return { request, connected, create, update, get, list, count, remove };
}
